import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiErrorCode } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/hooks/useAuth';
import { apiErrorCode, apiErrorMessage } from '@/services/apiClient';

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isRestoring } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantCode, setTenantCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The API asks for a company code only when one email exists in several tenants.
  const [needsTenantCode, setNeedsTenantCode] = useState(false);

  if (!isRestoring && isAuthenticated) {
    const state = location.state as LocationState | null;
    return <Navigate to={state?.from ?? '/'} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await login({
        email,
        password,
        ...(tenantCode.trim() ? { tenantCode: tenantCode.trim() } : {}),
      });

      const state = location.state as LocationState | null;
      void navigate(state?.from ?? '/', { replace: true });
    } catch (caught) {
      const code = apiErrorCode(caught);
      const message = apiErrorMessage(caught);

      if (code === ApiErrorCode.INVALID_CREDENTIALS && message?.includes('company code')) {
        setNeedsTenantCode(true);
        setError(message);
      } else if (code === ApiErrorCode.INVALID_CREDENTIALS) {
        setError(t('auth.invalidCredentials'));
      } else {
        setError(message ?? t('auth.genericError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="border-line rounded-xl border bg-white p-6 shadow-sm">
      <div className="mb-6 text-center">
        <h2 className="text-ink text-lg font-semibold">{t('auth.signIn')}</h2>
        <p className="text-muted mt-1 text-sm">{t('auth.signInSubtitle')}</p>
      </div>

      <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4" noValidate>
        {error ? <Alert tone="error">{error}</Alert> : null}

        <Input
          type="email"
          name="email"
          autoComplete="username"
          required
          dir="ltr"
          label={t('auth.email')}
          placeholder={t('auth.emailPlaceholder')}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <Input
          type="password"
          name="password"
          autoComplete="current-password"
          required
          dir="ltr"
          label={t('auth.password')}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />

        {needsTenantCode ? (
          <Input
            type="text"
            name="tenantCode"
            dir="ltr"
            label={t('auth.companyCode')}
            hint={t('auth.companyCodeHint')}
            value={tenantCode}
            onChange={(event) => setTenantCode(event.target.value)}
          />
        ) : null}

        <Button type="submit" fullWidth size="lg" isLoading={isSubmitting}>
          {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
        </Button>
      </form>
    </div>
  );
}
